import * as React from 'react';
import GridLayout, { Layout } from 'react-grid-layout';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { Control, Controller, useFieldArray, UseFormSetValue } from 'react-hook-form';
import { uniqBy } from 'lodash';
import Section from '../../Forms/Section';
import Widget from '../Widgets/Widget';
import CardNameInput from './CardNameInput';
import {
  getTemplateSizeByCards,
  loadWidgetsFromLocalStorage,
  MAX_GRID_COLS,
  MAX_GRID_ROWS,
  storeWidgetsInLocalStorage,
  validateWidgetsInGrid,
} from '../OnlineTemplateService';
import ConfirmDialog from '../../ConfirmDialog';
import PreviewModal from './PreviewModal';
import usePermissions from '../../../hooks/usePermissions';
import {
  PERMISSION_ONLINE_MAP_TEMPLATES_ASSIGN,
  PERMISSION_ONLINE_MAP_TEMPLATES_CUSTOMER,
  PERMISSION_ONLINE_MAP_TEMPLATES_PRIVATE,
  PERMISSION_ONLINE_MAP_TEMPLATES_SYSTEM,
} from '../../../Services/PermissionsService';
import ConfirmToaster from '../../Forms/ConfirmToaster/ConfirmToaster';
import { selectedCustomersAtom } from '../../../store/recoil/customers';
import {
  CardError,
  CardItem,
  GridErrors,
  GridLayoutDroppingItem,
  GridSizeValue,
  OnlineTemplateFormValues,
} from './types';
import ShowGridErrors from './ShowGridErrors';
import { IWidget } from '../Widgets/types';
import { getWidgetsByLayout } from '../Widgets/WidgetService';
import useAnalytics from '../../../hooks/useAnalytics';

interface ICardsSectionProps {
  widgets: IWidget[];
  droppingWidget: GridLayoutDroppingItem | undefined;
  draggedWidget: string | null;
  setDraggedWidget: React.Dispatch<React.SetStateAction<string | null>>;
  setDroppingWidget: React.Dispatch<React.SetStateAction<GridLayoutDroppingItem | undefined>>;
  value: CardItem[];
  setValue: UseFormSetValue<OnlineTemplateFormValues>;
  control: Control<OnlineTemplateFormValues>;
  error: CardError[];
  isSubmitBtnClicked: boolean;
  resetSubmitBtnClicked: (isSubmitBnt: boolean) => void;
}

export default function CardsSection(props: ICardsSectionProps): JSX.Element {
  const { trackEvent } = useAnalytics();
  const { formatMessage: f } = useIntl();
  const intl = useIntl();
  const [clearGridIndex, setClearGridIndex] = React.useState<number | null>(null);
  const [deleteCardIndex, setDeleteCardIndex] = React.useState<number | null>(null);
  const [isAddCardButtonEnabled, setIsAddCardButtonEnabled] = React.useState<boolean>(true);
  const [gridErrors, setGridErrors] = React.useState<GridErrors | null>(null);
  const [activeTab, setActiveTab] = React.useState<number>(0);
  const [isPreviewModalShown, setIsPreviewModalShown] = React.useState<boolean>(false);
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);

  const access = usePermissions(
    [
      PERMISSION_ONLINE_MAP_TEMPLATES_CUSTOMER,
      PERMISSION_ONLINE_MAP_TEMPLATES_PRIVATE,
      PERMISSION_ONLINE_MAP_TEMPLATES_SYSTEM,
      PERMISSION_ONLINE_MAP_TEMPLATES_ASSIGN,
    ],
    selectedCustomers
  );

  const { fields, append, remove, update } = useFieldArray({
    control: props.control,
    name: 'cards',
  });

  const validateWidgets = (layout: Layout[], index: number): GridErrors | null => {
    const gridErrorsData = validateWidgetsInGrid(
      layout,
      MAX_GRID_COLS,
      MAX_GRID_ROWS,
      intl
    );
    if (gridErrorsData) {
      const newGridErrors = {
        ...gridErrors,
        [index]: gridErrorsData,
      };
      return newGridErrors;
    }
    return null;
  };

  const handleOnDropItem = (layout: Layout[], index: number) => {
    if (props.draggedWidget !== null) {
      props.setDraggedWidget(null);
    }
    const newCards = props.value;
    const layoutWithoutDuplicates = uniqBy(layout, 'i');
    newCards[index].widgets = layoutWithoutDuplicates; // layout;
    storeWidgetsInLocalStorage(newCards);
    setGridErrors(validateWidgets(layout, index));
    props.setValue('cards', newCards);
  };

  const handleAddCard = (
    event:
      | React.MouseEvent<HTMLAnchorElement, MouseEvent>
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault();

    if (fields.length <= 2) {
      append({
        id: null,
        name: f({ id: 'onlineTemplates.subPage.section.cardAndWidgetSettings.newTab.name' }),
        widgets: [],
      });
      setActiveTab(fields.length);
    }
  };

  const handleClearGrid = (index: number) => {
    const values = props.value;
    const newValue: CardItem = {
      id: values[index].id,
      name: values[index].name,
      widgets: [],
    };
    update(index, newValue);
    setClearGridIndex(null);

    const cardsInLocalStorage = loadWidgetsFromLocalStorage();
    if (cardsInLocalStorage && index !== null) {
      const newCards = [...values];
      newCards[index].widgets = [];
      storeWidgetsInLocalStorage(newCards);
    }
  };

  const handleDeleteCard = (index: number) => {
    if (fields.length !== 1) {
      remove(index);
      const newActiveTab = index - 1;
      setActiveTab(newActiveTab < 0 ? 0 : newActiveTab);
    }
    setDeleteCardIndex(null);
  };

  const handleLayoutChange = (layout: Layout[], index: number) => {
    const values = props.value;
    const layoutWithoutDuplicates = uniqBy(layout, 'i');
    const newCards = values.map((card: CardItem, i: number) => {
      if (i === index) {
        return {
          ...card,
          widgets: layoutWithoutDuplicates, // layout,
        };
      }
      return card;
    });
    setGridErrors(validateWidgets(layout, index));
    storeWidgetsInLocalStorage(newCards);
    props.setValue('cards', newCards);
  };

  const handleRemoveWidget = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();
    const gridName = event.currentTarget.getAttribute('data-grid-name');
    if (gridName) {
      let cardIndex: number | null = null;
      const cardsInLocalStorage = loadWidgetsFromLocalStorage();
      const values = props.value;
      values.forEach((card: CardItem, index: number) => {
        const widget = card.widgets.find((item: Layout) => item.i === gridName);
        if (widget) {
          cardIndex = index;
        }
      });
      if (cardsInLocalStorage) {
        if (cardIndex !== null) {
          const newCards = [...values];
          newCards[cardIndex].widgets = cardsInLocalStorage[cardIndex].filter(
            (layout: Layout) => layout.i !== gridName
          );
          storeWidgetsInLocalStorage(newCards);
        }
      }
      if (cardIndex !== null) {
        const newCards = [...values];
        newCards[cardIndex].widgets = values[cardIndex].widgets.filter(
          (layout: Layout) => layout.i !== gridName
        );
        props.setValue('cards', newCards);
      }
    }
  };

  const handleClearGridConfirmCancelAction = () => {
    trackEvent(['Clear grid confirm dialog', 'click', 'Cancel clear']);
    setClearGridIndex(null);
  };

  const handleDeleteCardConfirmCancelAction = () => {
    trackEvent(['Delete card modal', 'click', 'Cancel delete']);
    setDeleteCardIndex(null);
  };

  const handleSelectTab = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    const tab = event.currentTarget.getAttribute('data-tab');
    if (tab) {
      setActiveTab(Number(tab));
    }
  };

  const handleClosePreviewModal = () => {
    trackEvent(['Preview card modal', 'click', 'Close modal']);
    setIsPreviewModalShown(false);
  };

  React.useEffect(() => {
    if (fields.length <= 2) {
      setIsAddCardButtonEnabled(true);
    }
    if (fields.length >= 3) {
      setIsAddCardButtonEnabled(false);
    }
  }, [fields.length]);

  const gridSize: GridSizeValue = getTemplateSizeByCards(props.value);

  const getDataForPreview = (): CardItem[] => {
    const cardsInLocalStorage = loadWidgetsFromLocalStorage();

    const values = props.value;
    const cards = values.map((card: CardItem, index: number) => {
      if (cardsInLocalStorage) {
        const newCard = {
          ...card,
          widgets: cardsInLocalStorage[index],
        };
        return newCard;
      }
      return card;
    });
    return cards;
  };

  const handleErrorsInTab = (index: number): boolean => {
    return !!props.error?.[index]?.name;
  };

  const handleCloseToast = () => {
    trackEvent(['Card section', 'click', 'Close toast']);
    props.resetSubmitBtnClicked(false);
  };

  const getWidgetsByPermission = (widgets): Layout[] => {
    if (access.canCreate || access.canUpdate) {
      return widgets;
    }

    return widgets.map((widget: Layout) => ({
      ...widget,
      static: true,
    }));
  };

  return (
    <Section
      id="cards-settings"
      title={f({ id: 'onlineTemplates.subPage.section.cardAndWidgetSettings.title' })}
    >
      {isPreviewModalShown && (
        <PreviewModal
          id={`preview-modal-${activeTab}`}
          title={f(
            { id: 'onlineTemplates.subPage.section.cardAndWidgetSettings.modal.title' },
            { cardName: props.value[activeTab].name }
          )}
          closeCallback={handleClosePreviewModal}
          cards={getDataForPreview()}
          activeTab={activeTab}
          gridSize={gridSize}
          widgets={props.widgets}
        />
      )}
      <div className="row">
        <div className="col-auto overflow-auto">
          <div className="b-widgets-info">
            <div className="b-tabs-toggler-template b-tabs-toggler-template--borders-on">
              <ul>
                {props.value.map((card: CardItem, index: number) => (
                  <li
                    className={`${
                      activeTab === index ? 'active-template' : 'tab-not-active'
                    } ${handleErrorsInTab(index) ? 'tab-error  ' : ''}`}
                    key={`card-tab-${index}`}
                  >
                    {props.isSubmitBtnClicked && handleErrorsInTab(index) && (
                      <ConfirmToaster
                        id="card-section-toaster"
                        title={f({
                          id: 'onlineTemplates.subPage.section.cardAndWidgetSettings.error.toast.title',
                        })}
                        type="error"
                        onClose={handleCloseToast}
                        duration={5000}
                        width="auto"
                        message={f({
                          id: 'onlineTemplates.subPage.section.cardAndWidgetSettings.error.toast.message',
                        })}
                      />
                    )}
                    <a href="#" title={card.name} onClick={handleSelectTab} data-tab={index}>
                      {handleErrorsInTab(index) && <i className="cmd-icon cmd-icon-error" />}
                      {card.name || ''}
                      {handleErrorsInTab(index) && (
                        <span className="error-text">
                          {f({ id: 'form.rules.inputIsRequired' })}
                        </span>
                      )}
                    </a>
                  </li>
                ))}
                {isAddCardButtonEnabled && (
                  <li className="tab-actions-template">
                    <button
                      type="button"
                      title={f({
                        id: 'onlineTemplates.subPage.section.cardAndWidgetSettings.addBookmark',
                      })}
                      onClick={handleAddCard}
                    >
                      <i className="cmd-icon cmd-icon-add" />
                      <span className="sr-only">
                        {f({
                          id: 'onlineTemplates.subPage.section.cardAndWidgetSettings.addBookmark',
                        })}
                      </span>
                    </button>
                  </li>
                )}
              </ul>
            </div>
          </div>
          {fields.map((card, index) => (
            <React.Fragment key={card.id}>
              {clearGridIndex !== null && (
                <ConfirmDialog
                  title={f({
                    id: 'onlineTemplates.subPage.section.cardAndWidgetSettings.clearGridConfirmDialog.title',
                  })}
                  cancelCallback={handleClearGridConfirmCancelAction}
                  continueCallback={() => {
                    trackEvent(['Clear grid confirm dialog', 'click', 'Confirm clear']);
                    handleClearGrid(clearGridIndex)
                  }}
                  cancelButtonTitle={f({
                    id: 'onlineTemplates.subPage.section.cardAndWidgetSettings.clearGridConfirmDialog.cancel',
                  })}
                  continueButtonTitle={f({
                    id: 'onlineTemplates.subPage.section.cardAndWidgetSettings.clearGridConfirmDialog.continue',
                  })}
                >
                  {f({
                    id: 'onlineTemplates.subPage.section.cardAndWidgetSettings.clearGridConfirmDialog.message',
                  })}
                </ConfirmDialog>
              )}
              {deleteCardIndex !== null && (
                <ConfirmDialog
                  title={f({
                    id: 'onlineTemplates.subPage.section.cardAndWidgetSettings.deleteCardConfirmDialog.title',
                  })}
                  cancelCallback={handleDeleteCardConfirmCancelAction}
                  continueCallback={() => {
                    trackEvent(['Delete card modal', 'click', 'Confirm delete']);
                    handleDeleteCard(deleteCardIndex)
                  }}
                  cancelButtonTitle={f({
                    id: 'onlineTemplates.subPage.section.cardAndWidgetSettings.deleteCardConfirmDialog.cancel',
                  })}
                  continueButtonTitle={f({
                    id: 'onlineTemplates.subPage.section.cardAndWidgetSettings.deleteCardConfirmDialog.continue',
                  })}
                >
                  {f({
                    id: 'onlineTemplates.subPage.section.cardAndWidgetSettings.deleteCardConfirmDialog.message',
                  })}
                </ConfirmDialog>
              )}
              <div
                className="b-widget-grid-content b-widget-grid-content--scrollable"
                style={activeTab !== index ? { display: 'none' } : {}}
              >
                <div className="b-widget-grid-content-header">
                  <Controller
                    name={`cards.${index}.name`}
                    control={props.control}
                    render={({ field }) => (
                      <CardNameInput
                        {...field}
                        id={`cards.${index}.name`}
                        name={`cards.${index}.name`}
                        label={f({
                          id: 'onlineTemplates.subPage.section.cardAndWidgetSettings.form.cardName',
                        })}
                        placeholder={f({
                          id: 'onlineTemplates.subPage.section.cardAndWidgetSettings.form.cardName.placeholder',
                        })}
                        value={props.value}
                        error={props.error}
                        setValue={props.setValue}
                        index={index}
                        gridErrors={gridErrors}
                        deleteCardCallback={() => setDeleteCardIndex(index)}
                        deleteCardTooltip={f({
                          id: 'onlineTemplates.subPage.section.cardAndWidgetSettings.button.deleteCard.tooltip',
                        })}
                        clearGridCallback={() => setClearGridIndex(index)}
                        clearGridTooltip={f({
                          id: 'onlineTemplates.subPage.section.cardAndWidgetSettings.button.clearGrid.tooltip',
                        })}
                        previewGridCallback={() => setIsPreviewModalShown(true)}
                        previewGridTooltip={f({
                          id: 'onlineTemplates.subPage.section.cardAndWidgetSettings.button.previewGrid.tooltip',
                        })}
                      />
                    )}
                  />
                </div>
                {gridErrors && <ShowGridErrors index={index} data={gridErrors} />}
                <div className={`w-widget-grid ${gridErrors ? 'grid-error-border' : ''}`}>
                  <GridLayout
                    allowOverlap={false}
                    preventCollision={true}
                    useCSSTransforms={false}
                    compactType="vertical"
                    className="b-widget-grid grid-background"
                    style={{
                      gridTemplateColumns: `repeat(12, 1fr)`,
                      width: 12 * 72 + 44,
                      height: 72 * 3 + 8,
                    }}
                    containerPadding={[0, 0]}
                    layout={getWidgetsByPermission(card.widgets)}
                    cols={12}
                    maxRows={3}
                    rowHeight={72}
                    width={12 * 72 + 44}
                    isResizable={false}
                    isDroppable={true}
                    margin={[4, 4]}
                    onDrop={(layout: Layout[]) => {
                      handleOnDropItem(layout, index);
                    }}
                    onLayoutChange={(layout: Layout[]) => {
                      handleLayoutChange(layout, index);
                    }}
                    droppingItem={props.droppingWidget}
                  >
                    {getWidgetsByLayout(props.widgets, card.widgets).map(
                      (widget: IWidget, widgetIndex: number) => (
                        <div key={widget.gridName}>
                          <Widget
                            key={`${widgetIndex}-${widget.gridName}`}
                            index={widgetIndex}
                            data={widget}
                            setDraggedWidget={props.setDraggedWidget}
                            setDroppingWidget={props.setDroppingWidget}
                            isMockupMode={true}
                            isInGrid={access.canCreate || access.canUpdate || false}
                            onRemoveCallback={handleRemoveWidget}
                          />
                        </div>
                      )
                    )}
                  </GridLayout>
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </Section>
  );
}
